/**
 * useDisplayPrefs · v2.8.5
 * ─────────────────────────
 * Shared display-density + text-size preference engine for the Admin Console
 * and the Driver app. Manages three preferences:
 *   · compact       — manual "Compact" spacing toggle (bool)
 *   · autoCompact   — opt-in "Auto" mode: compact spacing kicks in on small
 *                     screens (bool)
 *   · fontScale     — "sm" | "md" | "lg" text-size zoom
 *
 * Persistence is layered:
 *   1. localStorage for instant first-paint (no flash on reload).
 *   2. Backend account sync via caller-supplied fetchPrefs()/persistPrefs()
 *      so the preference follows the operator across devices/browsers.
 *
 * `effectiveCompact` is what the UI should actually apply: when autoCompact
 * is on it reflects the live viewport width; otherwise it mirrors the manual
 * `compact` toggle.
 */
import { useState, useEffect, useCallback, useRef } from "react";

const AUTO_COMPACT_BREAKPOINT = 1280; // px — below this, Auto mode compacts
const VALID_SCALES = ["sm", "md", "lg"];

const _readLS = (k, fallback) => {
  try {
    const v = window.localStorage.getItem(k);
    return v == null ? fallback : v;
  } catch { return fallback; }
};
const _writeLS = (k, v) => {
  try { window.localStorage.setItem(k, v); } catch { /* quota / private mode */ }
};

export default function useDisplayPrefs({ prefix, fetchPrefs, persistPrefs }) {
  const K = {
    compact: `${prefix}_compact`,
    auto:    `${prefix}_auto_compact`,
    font:    `${prefix}_font_scale`,
  };

  const [compact, setCompactState] = useState(() => _readLS(K.compact, "0") === "1");
  const [autoCompact, setAutoState] = useState(() => _readLS(K.auto, "0") === "1");
  const [fontScale, setFontState] = useState(() => {
    const v = _readLS(K.font, "md");
    return VALID_SCALES.includes(v) ? v : "md";
  });
  const [viewportW, setViewportW] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1920)
  );
  const hydratedRef = useRef(false);

  // Track viewport width so Auto mode can react to resizes live.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Hydrate from the backend account once on mount. Server wins over the
  // local snapshot so a fresh browser inherits the operator's saved choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchPrefs?.();
        if (cancelled || !p) return;
        if (typeof p.compact === "boolean") {
          setCompactState(p.compact); _writeLS(K.compact, p.compact ? "1" : "0");
        }
        if (typeof p.autoCompact === "boolean") {
          setAutoState(p.autoCompact); _writeLS(K.auto, p.autoCompact ? "1" : "0");
        }
        if (VALID_SCALES.includes(p.fontScale)) {
          setFontState(p.fontScale); _writeLS(K.font, p.fontScale);
        }
      } catch { /* keep localStorage snapshot */ }
      finally { hydratedRef.current = true; }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCompact = useCallback((v) => {
    const next = !!v;
    setCompactState(next); _writeLS(K.compact, next ? "1" : "0");
    persistPrefs?.({ compact: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistPrefs]);

  const setAutoCompact = useCallback((v) => {
    const next = !!v;
    setAutoState(next); _writeLS(K.auto, next ? "1" : "0");
    persistPrefs?.({ autoCompact: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistPrefs]);

  const setFontScale = useCallback((v) => {
    if (!VALID_SCALES.includes(v)) return;
    setFontState(v); _writeLS(K.font, v);
    persistPrefs?.({ fontScale: v });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistPrefs]);

  const effectiveCompact = autoCompact
    ? viewportW < AUTO_COMPACT_BREAKPOINT
    : compact;

  return {
    compact,
    autoCompact,
    fontScale,
    effectiveCompact,
    setCompact,
    setAutoCompact,
    setFontScale,
  };
}
